import { forwardRef, RefObject } from 'react';

import {
  Button,
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  HStack,
  Portal,
  Radio,
  RadioGroup,
  Text,
  VStack,
} from '@chakra-ui/react';

import { Controller, useForm } from 'react-hook-form';

import { yupResolver } from '@hookform/resolvers/yup';

import { useImportEmployeesStore } from 'app/store';

import { ArrowRightOutline, shadowStyles, textStyles } from '@aslanpay/portal-common-lib';

import { importEmployeesFileTypeFormSchema } from 'app/utils/schemas';

import { SelectFileTypeFormFields, ImportEmployeesStepProps } from '../types';

const SelectFileType = forwardRef<HTMLElement, ImportEmployeesStepProps>(
  ({ id, onStepAction }, ref) => {
    const { importEmployeesData, setImportEmployeesData } = useImportEmployeesStore.getState();

    const {
      control,
      handleSubmit,
      formState: { errors, isValid },
    } = useForm<SelectFileTypeFormFields>({
      mode: 'onBlur',
      defaultValues: {
        fileType: importEmployeesData.fileType || 'EMPLOYEE_DATA_FILE',
      },
      resolver: yupResolver(importEmployeesFileTypeFormSchema),
    });

    const handleNextStep = ({ fileType }: SelectFileTypeFormFields) => {
      setImportEmployeesData({
        ...importEmployeesData,
        fileType,
      });

      onStepAction();
    };

    return (
      <Flex
        flexDir="column"
        maxW="572px"
        alignSelf="center"
        gap="30px"
        borderRadius="10px"
        boxShadow={shadowStyles.dropdownMenu}
      >
        <form id={id} onSubmit={handleSubmit(handleNextStep)} noValidate>
          <FormControl isInvalid={!!errors?.fileType?.message} isRequired>
            <HStack
              minH="65px"
              px="40px"
              py="20px"
              borderBottom="1px solid"
              borderColor="brand.lightGray1"
              alignItems="center"
              justify="space-between"
            >
              <FormLabel as="h5" sx={{ ...textStyles.h5Bold }} margin="0">
                Select file type
              </FormLabel>
            </HStack>
            <Flex flexDir="column" px="40px" py="20px" gap="20px">
              <Text color="brand.mutedPurple" textStyle="smallMedium">
                Choose the type of file you want to upload for {importEmployeesData.companyName}.
              </Text>

              <Controller
                control={control}
                name="fileType"
                render={({ field: { onChange, value } }) => (
                  <RadioGroup onChange={onChange} value={value}>
                    <VStack spacing="20px" align="stretch">
                      <Radio value="EMPLOYEE_DATA_FILE">
                        <VStack align="start" spacing="8px">
                          <Text textStyle="h6SemiBold" color="brand.black">
                            Employee Data File
                          </Text>
                          <Text textStyle="smallMedium" color="brand.mutedPurple">
                            Upload employee information including names, emails, salaries, and
                            employment dates.
                          </Text>
                        </VStack>
                      </Radio>

                      <Radio value="APPROVED_EARNINGS_TIMESHEET">
                        <VStack align="start" spacing="8px">
                          <Text textStyle="h6SemiBold" color="brand.black">
                            Approved Earnings Timesheet (AET)
                          </Text>
                          <Text textStyle="smallMedium" color="brand.mutedPurple">
                            Upload approved earnings data for hourly and daily workers including
                            hours, rates, and pay periods.
                          </Text>
                        </VStack>
                      </Radio>
                    </VStack>
                  </RadioGroup>
                )}
              />
              <FormErrorMessage>{errors?.fileType?.message}</FormErrorMessage>

              <Text alignSelf="flex-start" color="system.error" textStyle="extraSmallMedium">
                * mandatory
              </Text>
            </Flex>
          </FormControl>
          <Portal containerRef={ref as RefObject<HTMLElement>}>
            <Button
              form={id}
              type="submit"
              size="sm"
              isDisabled={!isValid}
              rightIcon={<ArrowRightOutline boxSize="20px" />}
            >
              Next
            </Button>
          </Portal>
        </form>
      </Flex>
    );
  }
);

SelectFileType.displayName = 'SelectFileType';

export default SelectFileType;
